<template>
  <div class="donation-modal" @click="$emit('close')">
    <div class="donation-modal-content" @click.stop>
      <div class="donation-header">
        <h3 class="donation-title">感谢您的支持</h3>
        <button class="modal-close-btn" @click="$emit('close')">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
      
      <div class="donation-body">
        <p class="donation-description">
          如果这个网站对您有帮助，欢迎请我喝杯咖啡☕
        </p>
        
        <!-- 二维码区域 -->
        <div class="qr-codes">
          <div class="qr-code-item">
            <div class="qr-code" @contextmenu.prevent @touchstart="startLongPress($event, 'fadian')" @touchend="endLongPress($event, 'fadian')" @mousedown="startLongPress($event, 'fadian')" @mouseup="endLongPress($event, 'fadian')">
              <img src="@/assets/images/donate.png" alt="" />
              <div class="qr-hint">长按扫码</div>
            </div>
            <span class="qr-label">为爱发电</span>
          </div>
        </div>
        
        <div class="donation-tips">
          <p>💡 长按二维码可保存图片进行扫码</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from 'vue'

// 定义emit
defineEmits<{
  close: []
}>()

const longPressTimer = ref<NodeJS.Timeout | null>(null)

const startLongPress = (_event: Event, type: string) => {
  // 清除之前的定时器
  if (longPressTimer.value) {
    clearTimeout(longPressTimer.value)
  }
  
  // 设置长按定时器
  longPressTimer.value = setTimeout(() => {
    handleLongPress(type)
  }, 500)
}

const endLongPress = (_event: Event, _type: string) => {
  // 清除长按定时器
  if (longPressTimer.value) {
    clearTimeout(longPressTimer.value)
    longPressTimer.value = null
  }
}

const handleLongPress = (type: string) => {
  // 这里可以添加长按后的处理逻辑
  // 可以在这里添加保存图片的逻辑
  // 或者显示一个提示
  const paymentName = type === 'wechat' ? '微信支付' : '支付宝'
  if (navigator.share) {
    navigator.share({
      title: `${paymentName} - 我爱纪录片`,
      text: `感谢您的支持！这是${paymentName}收款码`,
      url: window.location.href
    }).catch(() => {
      // 用户取消分享或分享失败
    })
  }
}

onUnmounted(() => {
  // 清理定时器
  if (longPressTimer.value) {
    clearTimeout(longPressTimer.value)
  }
})
</script>

<style scoped>
/* 捐赠弹窗样式 */
.donation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: 20px;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.donation-modal-content {
  background: white;
  border-radius: 20px;
  width: min(500px, 90vw);
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  z-index: var(--z-modal);
}

.donation-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
  margin-bottom: 20px;
}

.donation-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--dark-text);
  font-family: 'Georgia', 'Times New Roman', serif;
}

.donation-body {
  padding: 0 24px 24px 24px;
}

.donation-description {
  margin: 0 0 24px 0;
  font-size: 16px;
  color: var(--light-text);
  text-align: center;
  line-height: 1.6;
  font-family: inherit;
}

.qr-codes {
  display: flex;
  gap: 24px;
  justify-content: center;
  margin-bottom: 20px;
}

.qr-code-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.qr-code {
  width: 140px;
  height: 140px;
  border: 2px solid rgba(139, 115, 85, 0.3);
  border-radius: 12px;
  overflow: hidden;
  background: var(--light-bg);
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
  -webkit-user-select: none;
}

.qr-code:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 25px rgba(139, 115, 85, 0.2);
  border-color: var(--secondary-color);
}

.qr-code:active {
  transform: scale(0.98);
}

.qr-code img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.qr-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  white-space: nowrap;
}

.qr-code:hover .qr-hint {
  opacity: 1;
}

.qr-label {
  font-size: 14px;
  color: var(--light-text);
  font-weight: 500;
  font-family: inherit;
}

.donation-tips {
  text-align: center;
  padding: 16px;
  background: var(--light-bg);
  border-radius: 12px;
  margin-top: 16px;
}

.donation-tips p {
  margin: 0;
  font-size: 14px;
  color: var(--light-text);
  line-height: 1.5;
}

.modal-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: var(--z-content);
}

.modal-close-btn:hover {
  background: rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

.modal-close-btn svg {
  width: 20px;
  height: 20px;
  color: #666;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .donation-modal-content {
    width: min(450px, 90vw);
  }
}

@media (max-width: 480px) {
  .donation-modal-content {
    width: min(400px, 95vw);
    margin: 10px;
    border-radius: 16px;
  }
  
  .donation-header {
    padding: 20px 20px 0 20px;
  }
  
  .donation-body {
    padding: 0 20px 20px 20px;
  }
  
  .qr-codes {
    gap: 16px;
  }
  
  .qr-code {
    width: 120px;
    height: 120px;
  }
  
  .qr-label {
    font-size: 13px;
  }
  
  .donation-title {
    font-size: 18px;
  }
  
  .donation-description {
    font-size: 14px;
  }
}
</style>